import {
  BarChart3,
  LayoutDashboard,
  MessageSquare,
  Phone,
  Server,
  Users,
  type LucideIcon,
} from 'lucide-react';
import type { AppView } from '@/types';
import {
  canManageModule,
  canViewModule,
  coerceModuleLevel,
  defaultModuleAccess,
  normalizeModuleAccess as normalizeBackendModuleAccess,
  type ModuleAccess as BackendModuleAccess,
  type ModuleLevel,
  type TopLevelModule,
} from '../convex/modules';

export type ModuleId = AppView;

export type DashboardWidgetId =
  | 'quickActions'
  | 'liveWidgets'
  | 'todayStats'
  | 'analytics'
  | 'extensionStatus'
  | 'queueMonitor'
  | 'activeCalls'
  | 'inboundRouting'
  | 'recentCalls';

export type ModuleAccess = BackendModuleAccess;

export interface ModuleDefinition {
  id: ModuleId;
  label: string;
  icon: LucideIcon;
  backendModule?: TopLevelModule;
}

export interface DashboardWidgetDefinition {
  id: DashboardWidgetId;
  label: string;
}

export const MODULE_DEFINITIONS: ModuleDefinition[] = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, backendModule: 'dashboard' },
  { id: 'cms', label: 'CRM', icon: Users, backendModule: 'cms' },
  { id: 'reporting', label: 'Reporting', icon: BarChart3, backendModule: 'reporting' },
  { id: 'provisioning', label: 'Provisioning', icon: Server },
  { id: 'chat', label: 'Chat', icon: MessageSquare, backendModule: 'chat' },
];

export const PROVISIONING_MODULE_IDS = [
  'extensions',
  'trunks',
  'ivrs',
  'routes',
  'phones',
  'auditLog',
] as const;

export const DASHBOARD_WIDGET_DEFINITIONS: DashboardWidgetDefinition[] = [
  { id: 'quickActions', label: 'Quick Actions' },
  { id: 'liveWidgets', label: 'Live Widgets' },
  { id: 'todayStats', label: 'Today Stats' },
  { id: 'analytics', label: 'Analytics' },
  { id: 'extensionStatus', label: 'Extension Status' },
  { id: 'queueMonitor', label: 'Queue Monitor' },
  { id: 'activeCalls', label: 'Active Calls' },
  { id: 'inboundRouting', label: 'Inbound Routing' },
  { id: 'recentCalls', label: 'Recent Calls' },
];

export { coerceModuleLevel, canViewModule, canManageModule };
export type { ModuleLevel, TopLevelModule };

export function hasProvisioningAccess(
  moduleAccess?: { modules?: Partial<Record<string, boolean | ModuleLevel | string>> } | null
): boolean {
  if (!moduleAccess?.modules) return false;
  return PROVISIONING_MODULE_IDS.some((id) => {
    const level = coerceModuleLevel(moduleAccess.modules?.[id], 'off');
    return level !== 'off';
  });
}

export function normalizeModuleAccess(
  access?: {
    modules?: Partial<Record<string, boolean | ModuleLevel | string>>;
    dashboardWidgets?: Partial<Record<DashboardWidgetId, boolean>>;
  } | null
): ModuleAccess {
  return normalizeBackendModuleAccess(access);
}

export function getDefaultModuleAccess(): ModuleAccess {
  return defaultModuleAccess();
}

export function isNavModuleEnabled(access: ModuleAccess, module: ModuleDefinition): boolean {
  if (module.id === 'provisioning') return hasProvisioningAccess(access);
  if (!module.backendModule) return false;
  return canViewModule(access, module.backendModule);
}

export function getFirstEnabledModule(access: ModuleAccess): ModuleId | null {
  return MODULE_DEFINITIONS.find((module) => isNavModuleEnabled(access, module))?.id || null;
}

export function hasEnabledTopLevelModule(access: ModuleAccess): boolean {
  return MODULE_DEFINITIONS.some((module) => isNavModuleEnabled(access, module));
}

export const moduleSettingsIcon = Phone;